import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { UserService } from '@api-client';
import { getKeyByValue, TermType } from '@common';
import { ToastService } from '@front-share';
import {
  LepiDatePicker,
  LepiIcon,
  ModalService,
} from '@lepisode-ui/components';
import dayjs, { Dayjs } from 'dayjs';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { map } from 'rxjs';
import { CustomValidators } from '../../../../../../../libs/common/src/lib/custom-validators';
import { ButtonComponent } from '../../../components/button/button.component';
import { ContainerComponent } from '../../../components/container/container.component';
import { InputIdComponent } from '../../../components/input-id/input-id.component';
import { InputNameComponent } from '../../../components/input-name/input-name.component';
import { InputPasswordComponent } from '../../../components/input-password/input-password.component';
import { InputTelComponent } from '../../../components/input-tel/input-tel.component';
import { LabelComponent } from '../../../components/label/label.component';
import { ModalContainerComponent } from '../../../components/modal-container/modal-container.component';
import TermsModalModal from '../../layout/footer/terms-modal/terms-modal.modal';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ContainerComponent,
    ModalContainerComponent,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    ButtonComponent,
    LepiIcon,
    InputIdComponent,
    InputPasswordComponent,
    InputTelComponent,
    InputNameComponent,
    LepiDatePicker,
    LabelComponent,
  ],
  templateUrl: './sign-up.page.html',
  styleUrl: './sign-up.page.scss',
})
export default class SignUpPage {
  router = inject(Router);
  toastService = inject(ToastService);
  modalService = inject(ModalService);
  userService = inject(UserService);
  service = signal<boolean>(false);
  info = signal<boolean>(false);
  marketing = signal<boolean>(false);
  useService = inject(UserService);
  getKeyByValue = getKeyByValue;

  allTermsAgreed = computed(
    () => this.service() && this.info() && this.marketing(),
  );

  termType = TermType;
  types = signal(
    Object.entries(TermType).map(([key, value]) => ({
      key,
      value,
    })),
  );

  // 소셜 로그인 정보
  provider = injectQueryParams('provider');
  providerId = injectQueryParams('providerId');

  form = new FormGroup({
    userId: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    password: new FormControl('', {
      validators: [Validators.required, CustomValidators.password],
      nonNullable: true,
    }),
    passwordCheck: new FormControl('', {
      validators: [
        Validators.required,
        CustomValidators.password,
        CustomValidators.isEqual('password'),
      ],
      nonNullable: true,
    }),
    tel: new FormControl('', {
      validators: [Validators.required, CustomValidators.tel],
      nonNullable: true,
    }),
    birthDay: new FormControl<any>(Dayjs, {
      validators: [Validators.required],
      nonNullable: false,
    }),
    name: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  formValid = toSignal(this.form.valueChanges.pipe(map(() => this.form.valid)));
  validForSubmission = computed(
    () => this.service() && this.info() && this.formValid(),
  );

  /**
   * 약관 전체 동의
   */
  toggleAllTerms() {
    if (this.allTermsAgreed()) {
      this.service.set(false);
      this.info.set(false);
      this.marketing.set(false);
    } else {
      this.service.set(true);
      this.info.set(true);
      this.marketing.set(true);
    }
  }

  async submit() {
    if (this.form.invalid) return;

    const authentication = this.providerId()
      ? {
          provider: this.provider(),
          providerId: this.providerId(),
        }
      : null;

    const body = {
      ...this.form.getRawValue(),
      birthDay: dayjs(this.form.getRawValue().birthDay).toDate(),
      authentication,
    };

    this.userService
      .userControllerCreate({
        body,
      })
      .subscribe({
        next: () => {
          this.toastService.success('회원가입이 완료되었습니다.');
          this.modalService.dismiss();
          this.router.navigateByUrl('/login', {
            replaceUrl: true,
          });
        },
      });
  }

  //약관 상세
  handleDetail(type: any) {
    this.modalService.create({
      component: TermsModalModal,
      componentProps: {
        type,
      },
    });
  }

  back() {
    this.router.navigate(['/login']);
  }
}
